import { useEffect, useMemo, useState } from 'react'
import {
  useQuery as useGqtyQuery,
  query as gqtyQuery,
  resolved,
} from '@hapana/shared/web/services/gqty'

import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'

import { useErrorHandler } from './errors.hooks'
import { useSiteId, useSiteIdResolved } from './site.hooks'

const fetchClientNotes = (siteId, clientId) =>
  resolved(
    () => {
      const result = gqtyQuery.clientNotes({ clientId, siteId })
      const notes = result?.notes ?? []
      notes.forEach((note) => {
        void note?.noteId
        void note?.content
        void note?.createdAt
        void note?.createdByName
      })
      return notes
    },
    { refetch: true }
  )

export const useClientNotesAndProperties = (clientId) => {
  const siteId = useSiteIdResolved()
  const [data, setData] = useState({ clientSiteNotes: [], clientSiteAlertedProperties: [] })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!clientId || !siteId) return

    let cancelled = false
    setIsLoading(true)

    fetchClientNotes(siteId, clientId)
      .then((notes) => {
        if (cancelled) return
        setData({
          clientSiteNotes: (notes || []).map((note) => ({
            id: note?.noteId,
            text: note?.content,
            date: note?.createdAt,
            name: note?.createdByName,
          })),
          clientSiteAlertedProperties: [],
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading, error: null, refetch: () => Promise.resolve(null) }
}

export const useClientPendingAgreements = (clientId) => {
  const { data: siteId } = useSiteId()
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const query = useGqtyQuery({
    suspense: false,
    cachePolicy: 'no-cache',
  })
  const shouldFetch = Boolean(clientId && siteId)

  useEffect(() => {
    if (query.$state.error && shouldFetch) {
      onError(query.$state.error)
    }
  }, [query.$state.error, onError, shouldFetch])

  const pendingAgreements = shouldFetch
    ? query.client({ id: clientId, siteId })?.client?.pendingAgreements
    : []

  const data = useMemo(() => {
    if (!shouldFetch) return []

    return (pendingAgreements || []).map((agreement, idx) => ({
      // packageID is aliased from agreementName in the DB query
      id: agreement?.packageSubscriptionID || `agreement-${idx}`,
      name: agreement?.packageID || 'Pending Agreement',
    }))
  }, [pendingAgreements, shouldFetch])

  return {
    data,
    isLoading: shouldFetch ? query.$state.isLoading : false,
  }
}

const ACTIVE_PACKAGE_STATUSES = ['Active', 'PendingStartDate']

const fetchActivePackages = (siteId, clientId) =>
  resolved(
    () => {
      const result = gqtyQuery.clientServices({
        clientId,
        siteId,
        status: ACTIVE_PACKAGE_STATUSES,
        type: ['PACKAGE', 'MEMBERSHIP'],
        pageSize: 20,
      })
      void result?.pageInfo?.totalRecords
      const rows = result?.results ?? []
      rows.forEach((r) => {
        void r.cursor
        void r.row?.serviceId
        void r.row?.serviceTemplateName
        void r.row?.serviceTemplateType
        void r.row?.siteId
        void r.row?.clientId
        void r.row?.status
        void r.row?.creditBalanceSnapshot?.totalBalance
        void r.row?.creditBalanceSnapshot?.hasUnlimited
        void r.row?.lastUsedOn
        void r.row?.creditBalanceSnapshot?.allocations
        r.row?.creditBalanceSnapshot?.allocations?.forEach((a) => {
          void a?.allocated
          void a?.consumed
          void a?.balance
        })
      })
      return rows
    },
    { refetch: true }
  )

const mapPackageRow = (r, siteName = '', v1ClientId = null) => {
  const snapshot = r.row?.creditBalanceSnapshot
  const firstAlloc = snapshot?.allocations?.[0]
  const creditsTotal = snapshot?.hasUnlimited ? 1000 : (firstAlloc?.allocated ?? 0)
  const creditsConsumed = firstAlloc?.consumed ?? 0

  return {
    id: r.row?.serviceId,
    siteId: r.row?.siteId ?? null,
    clientId: r.row?.clientId ?? null,
    creditsConsumed,
    creditsTotal,
    lastUsedOn: r.row?.lastUsedOn ?? null,
    packageDefinition: {
      name: r.row?.serviceTemplateName || '',
      type: r.row?.serviceTemplateType || '',
    },
    site: siteName ? { name: siteName } : null,
    client: v1ClientId != null ? { v1Id: v1ClientId } : null,
  }
}

export const useClientActivePackages = (clientId) => {
  const siteId = useSiteIdResolved()
  const [data, setData] = useState({ clientSiteActivePackages: [] })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!clientId || !siteId) return

    let cancelled = false
    setIsLoading(true)

    Promise.all([fetchActivePackages(siteId, clientId), fetchSiteName(siteId)])
      .then(([rows, siteName]) => {
        if (cancelled) return
        setData({
          clientSiteActivePackages: (rows || [])
            .filter((r) => r.row?.serviceId)
            .map((r) => mapPackageRow(r, siteName)),
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading, error: null, refetch: () => Promise.resolve(null) }
}

const FAILED_STATUSES = ['Failed', 'FailedRetry']
const OUTSTANDING_STATUSES = ['Staged', 'Scheduled', 'Processing', 'RequiresAction']

const fetchInvoicesByStatuses = (siteId, clientId, statuses) =>
  resolved(
    () => {
      const result = gqtyQuery.invoices({
        siteId,
        pageSize: 20,
        filter: { clientId, statuses },
      })
      void result?.pageInfo?.totalRecords
      const rows = result?.results ?? []
      rows.forEach((r) => {
        void r.cursor
        void r.row?.invoiceId
        void r.row?.description
        void r.row?.membershipName
        void r.row?.productName
        void r.row?.totalAmount
        void r.row?.currency
        void r.row?.createdAt
        void r.row?.saleDate
        void r.row?.siteId
        void r.row?.isRecurring
      })
      return rows
    },
    { refetch: true }
  )

const fetchSiteName = (siteId) =>
  resolved(
    () => {
      return gqtyQuery.site({ siteId })?.name ?? ''
    },
    { refetch: true }
  )

const toISODatetime = (v) => {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(String(v))
  if (isNaN(d.getTime())) return null
  return d.toISOString()
}

const mapInvoiceRow = (r, siteName = '') => ({
  id: r.row?.invoiceId,
  invoiceId: r.row?.invoiceId,
  label: r.row?.membershipName || r.row?.description || r.row?.productName || '',
  currency: r.row?.currency || 'AUD',
  price: r.row?.totalAmount,
  siteName,
  datetime: toISODatetime(r.row?.createdAt) || toISODatetime(r.row?.saleDate) || null,
  v1PaymentType: r.row?.isRecurring ? 'RECURRING' : 'ONE_TIME',
})

export const useClientFailedPayments = (clientId) => {
  const siteId = useSiteIdResolved()
  const [data, setData] = useState({ failedPayments: [], outstandingPayments: [] })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!clientId || !siteId) return

    let cancelled = false
    setIsLoading(true)

    Promise.all([
      fetchInvoicesByStatuses(siteId, clientId, FAILED_STATUSES),
      fetchInvoicesByStatuses(siteId, clientId, OUTSTANDING_STATUSES),
      fetchSiteName(siteId),
    ])
      .then(([failedRows, outstandingRows, siteName]) => {
        if (cancelled) return
        setData({
          failedPayments: (failedRows || [])
            .filter((r) => r.row?.invoiceId)
            .map((r) => mapInvoiceRow(r, siteName)),
          outstandingPayments: (outstandingRows || [])
            .filter((r) => r.row?.invoiceId)
            .map((r) => mapInvoiceRow(r, siteName)),
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading }
}
